import { useEffect, useState } from "react";
import FundingChart, { type FundingChartRow } from "./FundingChart";
import FundingTable from "../components/FundingTable";
import AssetValueChartFilter from "v2/features/plans/components/AssetValueChartFilter";
import { getSessionStore } from "app/utils/sessionStore";
import { getObjectFromLocalStore } from "app/utils/localStore";
import { makeRequest } from "app/utils/makeRequest";
import { THEME, USERTYPE, ProposalTypes } from "app/utils/types";
import TrueIqIcon from "app/assets/IconComponent/TrueIqIcon";
import { GoTriangleDown, GoTriangleUp } from "react-icons/go";
import { Accordion, AccordionTab } from "primereact/accordion";
import { AnimatedCheckbox } from "app/uicomponents/microcomponents/AnimatedChecbox";
import { Card } from "primereact/card";
import ProjectionsIcon from "app/assets/IconComponent/ProjectionsIcon";
import FundingLedger from "./FundingLedger";

// ── Types ─────────────────────────────────────────────────────────────────────

interface InsuredPerson {
  id: string;
  name: string;
}

type FundingRow = FundingChartRow;

interface ApiGraphItem {
  planYear: number;
  year: number;
  insuredAge: number;
  clientPremium: { actual: number; illustrated: number; trueIQIndex: number };
  lenderPremium: { actual: number; illustrated: number; trueIQIndex: number };
  interestPayment: { actual: number; illustrated: number; trueIQIndex: number };
  planFee: { actual: number; illustrated: number; trueIQIndex: number };
  originationFee: { illustrated: number };
  combinedTrueIQIndex: number;
  interestPaymentInvoiceStatus: string;
  lenderPremiumInvoiceStatus: string;
  planFeeInvoiceStatus: string;
}

interface ApiTableItem {
  planYear: number; // sequential (1, 2, 3…)
  year: number; // calendar year (2026)
  insuredAge: number;
  clientPremium: { actual: number; illustrated: number };
  lenderPremium: { actual: number; illustrated: number };
  interestPayment: { actual: number; illustrated: number };
  planFee: { actual: number; illustrated: number };
  originationFee: { actual: number; illustrated: number };
  total: { actual: number; illustrated: number };
}

// API planYear = sequential (1,2,3…), API year = calendar year (2026)
// FundingRow.year = sequential, FundingRow.planYear = calendar year
const mapToFundingRow = (item: ApiGraphItem): FundingRow => ({
  planYear: item.year,
  year: item.planYear,
  insuredAge: item.insuredAge,
  clientPremium: item.clientPremium.actual,
  clientPlanFee: item.planFee.actual,
  clientInterestPayment: item.interestPayment.actual,
  clientOriginationFees: item.originationFee?.illustrated ?? 0,
  clientOriginationFeesIllustrated: item.originationFee?.illustrated ?? 0,
  lenderPremium: item.lenderPremium.actual,
  lenderOriginationFees: 0,
  lenderOriginationFeesIllustrated: 0,
  totalPolicyPremium: item.clientPremium.actual + item.lenderPremium.actual,
  clientPremiumIllustrated: item.clientPremium.illustrated,
  lenderPremiumIllustrated: item.lenderPremium.illustrated,
  clientInterestPaymentIllustrated: item.interestPayment.illustrated,
  clientPlanFeeIllustrated: item.planFee.illustrated,
  combinedTrueIQIndex: item.combinedTrueIQIndex,
  interestPaymentInvoiceStatus: item.interestPaymentInvoiceStatus,
  lenderPremiumInvoiceStatus: item.lenderPremiumInvoiceStatus,
  planFeeInvoiceStatus: item.planFeeInvoiceStatus,
});

const mapTableItemToFundingRow = (item: ApiTableItem): any => ({
  planYear: item.year, // calendar year → "Plan Year" column
  year: item.planYear, // sequential    → "Year" column + row highlight
  insuredAge: item.insuredAge,
  clientPremium: item.clientPremium.actual,
  clientPlanFee: item.planFee.actual,
  clientInterestPayment: item.interestPayment.actual,
  clientOriginationFees: item.originationFee.actual,
  clientOriginationFeesIllustrated: item.originationFee.illustrated,
  lenderPremium: item.lenderPremium.actual,
  lenderOriginationFees: 0,
  lenderOriginationFeesIllustrated: 0,
  totalPolicyPremium: item.total.actual,
  clientPremiumIllustrated: item.clientPremium.illustrated,
  lenderPremiumIllustrated: item.lenderPremium.illustrated,
  clientInterestPaymentIllustrated: item.interestPayment.illustrated,
  clientPlanFeeIllustrated: item.planFee.illustrated,
});

// ── Filter config ─────────────────────────────────────────────────────────────

const PRIMARY = "#1E3A8A";

interface FilterOption {
  id: string;
  label: string;
  color: string;
  field: keyof Pick<
    FundingRow,
    | "clientPremium"
    | "clientPlanFee"
    | "clientInterestPayment"
    | "clientOriginationFees"
    | "lenderPremium"
  >;
}

const FILTER_OPTIONS: FilterOption[] = [
  { id: "CLIENT_PREMIUM", label: "Client Premium", color: PRIMARY, field: "clientPremium" }, // 100%

  {
    id: "LENDER_PREMIUM",
    label: "Lender Premium",
    color: `${PRIMARY}99`, // 60%
    field: "lenderPremium",
  },

  {
    id: "INTEREST_PAYMENT",
    label: "Interest Payment",
    color: `${PRIMARY}73`, // 45%
    field: "clientInterestPayment",
  },

  {
    id: "PLAN_FEE",
    label: "Plan Fee",
    color: `${PRIMARY}4D`, // 30%
    field: "clientPlanFee",
  },

  {
    id: "ORIGINATION_FEE",
    label: "Origination Fee",
    color: `${PRIMARY}26`, // 15%
    field: "clientOriginationFees",
  },
];

const BUY_SELL_FILTER_OPTIONS: FilterOption[] = [
  { id: "CLIENT_PREMIUM", label: "Life", color: PRIMARY, field: "clientPremium" },
  { id: "LENDER_PREMIUM", label: "Disability", color: `${PRIMARY}99`, field: "lenderPremium" },
  { id: "PLAN_FEE", label: "Plan Fees", color: `${PRIMARY}4D`, field: "clientPlanFee" },
];

// ── Sub-components ────────────────────────────────────────────────────────────

const accordionHeader = (
  title: string,
  projectionallow?: boolean,
  showProjections?: boolean,
  onToggle?: () => void,
) => (
  <div className="flex justify-between w-full items-center">
    <div className="text-lg conthrax-font ml-2 text-[#55556D]">{title}</div>
    {projectionallow && (
      <div
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <AnimatedCheckbox
          checked={showProjections ?? false}
          onChange={() => onToggle?.()}
          inputId="funding-projections"
          label={
            <span className="flex items-center gap-1">
              <span style={{ width: 39, height: 36, display: "inline-flex", flexShrink: 0 }}>
                <ProjectionsIcon fill={showProjections ? PRIMARY : "#64748B"} />
              </span>
            </span>
          }
          brandingColor={PRIMARY}
          size={20}
          hideBorder
        />
      </div>
    )}
  </div>
);

const collapseIcon = (
  <div className="rounded-full bg-[#EBEBEF] p-[1px] cursor-pointer">
    <GoTriangleDown className="text-[#8A8AA3]" size={27} style={{ transition: "transform 0.2s" }} />
  </div>
);
const expandIcon = (
  <div className="rounded-full bg-[#EBEBEF] p-[1px] cursor-pointer">
    <GoTriangleUp
      className="text-[#8A8AA3]"
      size={27}
      style={{ transition: "transform 0.2s", rotate: "140deg" }}
    />
  </div>
);
const globalTheme = getSessionStore("globalTheme");
// ── Main component ────────────────────────────────────────────────────────────

function FundingIntRev() {
  const isDark = getSessionStore("globalTheme") === THEME.DARK;
  // const navigate = useNavigate();
  const planId = getSessionStore("planInfo")?.planId;
  const isSplitDollar = getSessionStore("planInfo")?.planType === ProposalTypes.SplitDollar;
  const isBuySell = getSessionStore("planInfo")?.planType === ProposalTypes.BuySell;
  const activeFilterOptions = isBuySell ? BUY_SELL_FILTER_OPTIONS : FILTER_OPTIONS;

  const getFilterLabel = (id: string, defaultLabel: string) => {
    if (!isSplitDollar) return defaultLabel;
    if (id === "CLIENT_PREMIUM") return "Employee Premium";
    if (id === "LENDER_PREMIUM") return "Employer Premium";
    return defaultLabel;
  };
  const [selectedYear, setSelectedYear] = useState<number>(getSessionStore("ircSelectedYear") ?? 1);
  const [creditType, setCreditType] = useState<string>(
    getSessionStore("ircSelectedCreditType") ?? "preCredit",
  );

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail ?? {};
      const newYear =
        detail.year !== undefined ? detail.year : getSessionStore("ircSelectedYear") ?? 1;
      const newCreditType =
        detail.creditType !== undefined
          ? detail.creditType
          : getSessionStore("ircSelectedCreditType") ?? "preCredit";
      setSelectedYear(newYear);
      setCreditType(newCreditType);
    };
    window.addEventListener("ircSelectionChanged", handler);
    return () => window.removeEventListener("ircSelectionChanged", handler);
  }, []);

  const userAccess = getObjectFromLocalStore("useraccess");
  const isContact = userAccess?.userType === USERTYPE.CONTACT;

  const [insuredPersons, setInsuredPersons] = useState<InsuredPerson[]>([]);

  useEffect(() => {
    if (!planId) return;
    makeRequest("reviewCenterReview")
      .get(planId, true)
      .then((res: any) => {
        const list: any[] = res?.data?.data?.insuredList ?? [];
        setInsuredPersons(
          list.map((item: any) => ({
            id: item.userId,
            name: `${item.firstName ?? ""} ${item.lastName ?? ""}`.trim() || "Insured",
          })),
        );
      })
      .catch(() => {});
  }, [planId]);

  const [selectedInsured, setSelectedInsured] = useState<Set<string>>(new Set());
  const [activeFilters, setActiveFilters] = useState<Set<string>>(
    new Set(activeFilterOptions.map((f) => f.id)),
  );
  // const [selectedYear] = useState<number>(1);
  const [fundingData, setFundingData] = useState<FundingRow[]>([]);
  const [tableRows, setTableRows] = useState<FundingRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [showProjections, setShowProjections] = useState(true);
  const [showIndex, setShowIndex] = useState(false);
  const [selectedChartFilter, setSelectedChartFilter] = useState("year");

  // Seed selected insured once persons load
  useEffect(() => {
    if (insuredPersons.length > 0 && selectedInsured.size === 0) {
      setSelectedInsured(new Set(insuredPersons.map((p) => p.id)));
    }
  }, [insuredPersons, selectedInsured]);

  // When multiple insureds selected, force year filter; when single, revert to age
  useEffect(() => {
    if (selectedInsured.size > 1) {
      setSelectedChartFilter("year");
    } else {
      setSelectedChartFilter("insuredAge");
    }
  }, [selectedInsured]);

  useEffect(() => {
    // if (!planId || !selectedInsured) return;
    setLoading(true);
    // Always send ALL filter IDs so the backend computes combinedTrueIQIndex
    // across every funding component, regardless of which are toggled in the UI.
    const allFilterIds = JSON.stringify(FILTER_OPTIONS.map((f) => f.id));
    const insuredIds = JSON.stringify([...selectedInsured]);
    makeRequest("interactiveReviewCenterFunding")
      .get(
        `${planId}?filters=${allFilterIds}&year=${selectedYear}&creditType=${creditType}&insuredIds=${insuredIds}`,
        true,
      )
      .then((res: any) => {
        const graphData: ApiGraphItem[] = res?.data?.data?.graphData ?? [];
        setFundingData(graphData.map(mapToFundingRow));
        const tableData: ApiTableItem[] = res?.data?.data?.tableData ?? [];
        setTableRows(tableData.map(mapTableItemToFundingRow));
      })
      .catch((err: any) => console.error("Failed to fetch funding data:", err))
      .finally(() => setLoading(false));
  }, [planId, activeFilters, selectedYear, creditType, [...selectedInsured].join(",")]);

  const insuredName = insuredPersons
    .filter((p) => selectedInsured.has(p.id))
    .map((p) => p.name)
    .join(", ");

  const cardBg = isDark ? "bg-[#2A2A3D]" : "bg-white";
  const textSub = isDark ? "text-gray-400" : "text-gray-500";
  const toggleFilter = (id: string) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Card
      className={`pb-1 border border-1
       border-gray-300 rounded-2xl w-full min-h-screen
         shadow-lg dark:!border-[#3A3A50] dark:bg-[#2A2A3D] `}
    >
      <div className="flex flex-col gap-5 pb-8">
        {/* ── Insured persons + TrueIQ ─────────────────────────────────────── */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p
              className={`text-[18px] conthrax-font font-semibold uppercase tracking-wider mb-2 ${textSub}`}
            >
              {isBuySell ? "Shareholders" : "Insured Person(s)"}
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              {insuredPersons.length === 0 ? (
                <p className="text-sm text-gray-400">
                  {isBuySell ? "No shareholders" : "No person is insured"}
                </p>
              ) : (
                insuredPersons.map((person) => (
                  <AnimatedCheckbox
                    key={person.id}
                    checked={selectedInsured.has(person.id)}
                    onChange={
                      () =>
                        setSelectedInsured((prev) => {
                          const next = new Set(prev);
                          if (next.has(person.id)) {
                            if (next.size > 1) next.delete(person.id);
                          } else {
                            next.add(person.id);
                          }
                          return next;
                        })
                      // eslint-disable-next-line
                    }
                    inputId={person.id}
                    label={person.name}
                    brandingColor={PRIMARY}
                    size={20}
                  />
                ))
              )}
            </div>
          </div>

          {/* TrueIQ badge */}
          <div
            className="flex flex-col rounded-md items-center gap-1.5 px-5 py-2"
            style={{
              border: `1px solid ${PRIMARY}55`,
              color: isDark ? "#93C5FD" : PRIMARY,
              background: isDark ? `${PRIMARY}15` : `${PRIMARY}08`,
            }}
          >
            <div className="flex gap-1 items-center">
              <TrueIqIcon width={18} fill="#55556D" />
              <span className="text-md conthrax-font text-[#55556D]">TrueIQ</span>
            </div>

            <AnimatedCheckbox
              checked={showIndex}
              onChange={() => setShowIndex((v) => !v)}
              inputId="trueiq-index"
              label={isBuySell ? "Protect" : "Index"}
              brandingColor={PRIMARY}
              size={20}
              hideBorder
            />
          </div>
        </div>

        {/* ── Funding filter checkboxes ────────────────────────────────────── */}
        <div>
          <p
            className={`text-[18px] font-semibold uppercase conthrax-font tracking-wider mb-2 ${textSub}`}
          >
            Funding
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            {activeFilterOptions.map((f) => (
              <AnimatedCheckbox
                key={f.id}
                checked={activeFilters.has(f.id)}
                onChange={() => toggleFilter(f.id)}
                inputId={f.id}
                label={
                  <span className="flex items-center gap-2">
                    {getFilterLabel(f.id, f.label)}

                    <span
                      className="w-[12px] h-[12px] rounded-full inline-block"
                      style={{ backgroundColor: f.color }}
                    />
                  </span>
                }
                brandingColor={PRIMARY}
                size={20}
                borderExtra="1px solid #EBEBEF"
              />
            ))}
          </div>
        </div>

        <Accordion
          collapseIcon={collapseIcon}
          expandIcon={expandIcon}
          multiple
          activeIndex={isBuySell ? [0, 1, 2] : [0, 1]}
          className="bg-gray-100 dark:!bg-[#111111] rounded-md ip-project-accordion-main"
        >
          {isBuySell && (
            <AccordionTab
              headerTemplate={accordionHeader("Funding Ledger")}
              headerClassName="interactive-proposal-projection"
              className="bg-light-primary dark:bg-[#2A2A3D] rounded-md"
            >
              <FundingLedger isDark={isDark} />
            </AccordionTab>
          )}
          <AccordionTab
            headerTemplate={accordionHeader("Funding Chart", true, showProjections, () =>
              setShowProjections((v) => !v),
            )}
            headerClassName="interactive-proposal-projection"
            className="bg-light-primary dark:bg-[#2A2A3D] rounded-md"
          >
            <div className={`rounded-2xl shadow-md overflow-hidden ${cardBg}`}>
              <div className="px-4 pt-3 pb-2">
                {!loading && fundingData.length === 0 ? (
                  <div
                    className="flex justify-center items-center"
                    style={{
                      backgroundColor: globalTheme === THEME.DARK ? "#000000" : "#ffffff",
                      borderRadius: "8px",
                      padding: "5px",
                      height: "300px",
                    }}
                  >
                    <div className="text-center">
                      <i className="pi pi-chart-line text-3xl text-gray-400 mb-2" />
                      <p
                        className={`text-lg ${
                          globalTheme === THEME.DARK ? "text-gray-300" : "text-gray-500"
                        }`}
                      >
                        No data available
                      </p>
                    </div>
                  </div>
                ) : (
                  <FundingChart
                    data={fundingData}
                    activeFilters={activeFilters}
                    isDark={isDark}
                    selectedYear={selectedYear}
                    loading={loading}
                    showProjections={showProjections}
                    // onShowProjectionsChange={setShowProjections}
                    isContact={isContact}
                    showIndex={showIndex}
                    selectedChartFilter={selectedChartFilter}
                    isSplitDollar={isSplitDollar}
                  />
                )}
              </div>

              <div className="mt-1 px-1 pb-2">
                <AssetValueChartFilter
                  selectedChartFilter={selectedChartFilter}
                  onChartFilterChange={setSelectedChartFilter}
                  showProjections={showProjections}
                  onProjectionsToggle={() => setShowProjections((v) => !v)}
                  enableAgeFilter={selectedInsured.size <= 1}
                  hideProjection
                />
              </div>
              {insuredName && (
                <div className="flex items-center px-4 pb-3 gap-2">
                  <p className={`text-[11px] text-center flex-1 ${textSub}`}>
                    Plan Funding for {insuredName}
                  </p>
                </div>
              )}
            </div>
          </AccordionTab>

          <AccordionTab
            headerTemplate={accordionHeader("Funding Table")}
            headerClassName="interactive-proposal-projection"
            className="bg-light-primary dark:bg-[#2A2A3D] rounded-md"
          >
            <FundingTable
              data={tableRows}
              selectedYear={selectedYear}
              insuredName={insuredName}
              isDark={isDark}
              cardBg={cardBg}
              // divider={divider}
              // textMain={textMain}
              textSub={textSub}
              activeFilters={activeFilters}
              isSplitDollar={isSplitDollar}
            />
          </AccordionTab>
        </Accordion>
      </div>
    </Card>
  );
}

export default FundingIntRev;
